// REACT
import React from "react";

// MATERIAL UI
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";

// COMPONENTS
import NotabeneHostedMount from "src/components/wallets/dropdown/NotabeneHostedMount";
import TransferStatusPill, {
  statusToPillVariant,
} from "src/components/transfers/TransferStatusPill";
import { getCopy, getSlaCopy } from "src/components/transfers/copy";
import DepositActionRequiredForm from "src/components/transfers/DepositActionRequiredForm";

// API
import { useGetNotabeneTransferQuery } from "src/store/features/api/notabene";
import { useGetTrConfigQuery } from "src/store/features/api/generated";

// HOOKS
import { useUserSlice } from "src/store/features/user.slice";

interface TransferDetailContentProps {
  txId: string;
  /** Optional callback when info-request hosted widget fires complete. */
  onInfoRequestComplete?: () => void;
}

const isPendingReviewStatus = (status: string | undefined) => {
  if (!status) return false;
  return [
    "TR_AWAITING_DECISION",
    "TR_MANUAL_REVIEW",
    "PENDING_REVIEW",
    "PENDING",
    "AWAITING_AUTHORIZATION",
  ].includes(status.toUpperCase());
};

const isActionRequiredStatus = (status: string | undefined) => {
  if (!status) return false;
  return ["TR_INFO_REQUESTED", "ACTION_REQUIRED", "REQUIRES_PII"].includes(
    status.toUpperCase(),
  );
};

const isRejectedStatus = (status: string | undefined) => {
  if (!status) return false;
  return ["TR_DECLINED", "REJECTED"].includes(status.toUpperCase());
};

/**
 * Transfer detail content. Three sub-views (Pending Review / Action Required /
 * Rejected) selected from the persisted transfer's status. Uses entity-aware
 * copy from `copy.ts` and the config-driven SLA copy via `useGetTrConfigQuery`.
 */
export const TransferDetailContent: React.FC<TransferDetailContentProps> = ({
  txId,
  onInfoRequestComplete,
}) => {
  const { data: transfer, isLoading, refetch } = useGetNotabeneTransferQuery(txId, {
    skip: !txId,
  });
  const { data: trConfig } = useGetTrConfigQuery();
  const { entity } = useUserSlice() as unknown as { entity?: "AU" | "US" };

  // D18 — Notabene's hosted info-request widget can redirect users back to
  // this page with `?notabene_callback=1` after completion. Re-fetch + clean
  // the query string so a stale "info_requested" status doesn't linger.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("notabene_callback") === "1") {
      refetch();
      params.delete("notabene_callback");
      const cleaned = params.toString();
      const url =
        window.location.pathname + (cleaned ? `?${cleaned}` : "") + window.location.hash;
      window.history.replaceState({}, "", url);
    }
  }, [refetch]);

  if (isLoading || !transfer) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  const variant = statusToPillVariant(transfer.status);
  const status = transfer.status;

  return (
    <Stack spacing={3}>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="h5" fontWeight={600}>
          {/* The dialog also renders OUTGOING transfers (history links) —
              only an inbound row is a "deposit". */}
          {transfer.direction === "INCOMING" ? "Incoming Deposit" : "Transfer"}
        </Typography>
        {variant && <TransferStatusPill status={variant} size="md" />}
      </Box>

      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.secondary">
          Asset
        </Typography>
        <Typography variant="body1">
          {transfer.asset} — {transfer.amount}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Destination
        </Typography>
        <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
          {transfer.destinationAddress}
        </Typography>
      </Stack>

      {isPendingReviewStatus(status) && (
        <PendingReviewView
          entity={entity}
          direction={transfer.direction}
          pendingReviewBusinessDays={trConfig?.pendingReviewBusinessDays}
          counterpartyMismatch={Boolean(transfer.counterpartyMismatch)}
        />
      )}

      {isActionRequiredStatus(status) &&
        (transfer.direction === "INCOMING" ? (
          <DepositActionRequiredView
            txId={transfer.txId}
            asset={transfer.asset}
            sourceAddress={transfer.sourceAddress}
            amount={transfer.amount ? Number(transfer.amount) : undefined}
            entity={entity}
            onComplete={() => {
              refetch();
              onInfoRequestComplete?.();
            }}
          />
        ) : (
          <ActionRequiredView
            txId={transfer.txId}
            asset={transfer.asset}
            destinationAddress={transfer.destinationAddress}
            amount={transfer.amount ? Number(transfer.amount) : undefined}
            entity={entity}
            onComplete={() => {
              refetch();
              onInfoRequestComplete?.();
            }}
          />
        ))}

      {isRejectedStatus(status) && (
        <RejectedView
          entity={entity}
          isThirdPartyRejection={
            (transfer.reason ?? "").toLowerCase().includes("third") ||
            (transfer.reason ?? "").toLowerCase().includes("third_party")
          }
        />
      )}
    </Stack>
  );
};

// ----- sub-views -----------------------------------------------------------

const PendingReviewView: React.FC<{
  entity: "AU" | "US" | undefined;
  direction: "OUTGOING" | "INCOMING" | undefined;
  pendingReviewBusinessDays: number | undefined;
  counterpartyMismatch: boolean;
}> = ({ entity, direction, pendingReviewBusinessDays, counterpartyMismatch }) => {
  const sla = getSlaCopy(entity, pendingReviewBusinessDays);
  // D12 — flip copy for incoming deposits to the deposit-specific scope.
  const depositCopy =
    direction === "INCOMING" ? getCopy(entity, "pendingReviewDeposit") : null;
  const title = depositCopy?.title ?? sla.title;
  const body = depositCopy?.body ?? sla.body;
  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }} data-testid="pending-review-view">
        <Typography fontWeight={600} mb={0.5}>
          {title}
        </Typography>
        <Typography variant="body2">{body}</Typography>
      </Alert>
      {counterpartyMismatch && (
        <Alert severity="warning" data-testid="pending-review-mismatch">
          <Typography variant="body2">
            We're double-checking the recipient details on this transfer. This usually adds a
            day to the review.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

const DepositActionRequiredView: React.FC<{
  txId: string;
  asset: string | undefined;
  sourceAddress: string | undefined;
  amount: number | undefined;
  entity: "AU" | "US" | undefined;
  onComplete: () => void;
}> = ({ txId, asset, sourceAddress, amount, entity, onComplete }) => {
  const copy = getCopy(entity, "actionRequiredDeposit");
  const canMount = Boolean(asset && sourceAddress);
  return (
    <Box data-testid="deposit-action-required-view">
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography fontWeight={600} mb={0.5}>
          {copy.title}
        </Typography>
        <Typography variant="body2">{copy.body}</Typography>
      </Alert>
      {canMount ? (
        <DepositActionRequiredForm
          txId={txId}
          asset={asset as string}
          sourceAddress={sourceAddress as string}
          amount={amount}
          onComplete={onComplete}
        />
      ) : (
        <Alert severity="info" data-testid="deposit-action-required-missing-context">
          <Typography variant="body2">
            We couldn't load the customer info form — the transfer is missing source-address /
            asset context. Please contact support.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

const ActionRequiredView: React.FC<{
  txId: string;
  asset: string | undefined;
  destinationAddress: string | undefined;
  amount: number | undefined;
  entity: "AU" | "US" | undefined;
  onComplete: () => void;
}> = ({ txId, asset, destinationAddress, amount, entity, onComplete }) => {
  const copy = getCopy(entity, "infoRequest");
  // Notabene's SDK doesn't expose a standalone info-request widget — D18
  // recovery is handled by re-mounting createWithdrawalAssist with the
  // existing transactionId. The SDK detects the transfer's incomplete
  // state and surfaces just the missing fields. counterpartyAssist is
  // enabled so if the missing data needs to come from a counterparty
  // (third party or another device), the customer can share a link.
  const canMount = Boolean(asset && destinationAddress && amount);
  return (
    <Box data-testid="action-required-view">
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography fontWeight={600} mb={0.5}>
          {copy.title}
        </Typography>
        <Typography variant="body2">{copy.body}</Typography>
      </Alert>
      {canMount ? (
        <NotabeneHostedMount
          kind="withdrawal-assist"
          txId={txId}
          asset={asset}
          destinationAddress={destinationAddress}
          amount={amount}
          counterpartyAssist
          onComplete={onComplete}
          redirectFallbackUrl={`${window.location.origin}/transfers/${txId}?notabene_callback=1`}
        />
      ) : (
        <Alert severity="info" data-testid="action-required-missing-context">
          <Typography variant="body2">
            We couldn't load the recovery widget — the transfer is missing
            asset/amount/destination context. Please contact support.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

const RejectedView: React.FC<{
  entity: "AU" | "US" | undefined;
  isThirdPartyRejection: boolean;
}> = ({ entity, isThirdPartyRejection }) => {
  // D14 — copy is keyed by `rejectionReason` only today. When product
  // confirms deposit-specific variants of these scopes (the third-party-to-
  // third-party text differs by direction), reintroduce a `direction` prop
  // and switch on it here.
  const scope = isThirdPartyRejection ? "rejectionThirdParty" : "rejectionRegulatory";
  const copy = getCopy(entity, scope);
  return (
    <Box data-testid="rejected-view">
      <Alert severity="error" sx={{ mb: 2 }}>
        <Typography fontWeight={600} mb={0.5}>
          {copy.title}
        </Typography>
        <Typography variant="body2">{copy.body}</Typography>
      </Alert>
      {copy.primaryCta && (
        <Button
          variant="outlined"
          color="error"
          href="mailto:support@calebandbrown.com"
          data-testid="rejected-view-cta">
          {copy.primaryCta}
        </Button>
      )}
    </Box>
  );
};

export default TransferDetailContent;
